import {asyncNoop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {makeAnalyticsEventId} from 'analytics/Analytics';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import {assert} from 'Asserts';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import * as Context from 'Context';
import {Tag} from 'folder-items/FileConstants';
import {AssayRecordOriginType, Status} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import RunAutomationFileViewer from 'lab-automation/automation-files/RunAutomationFileViewer';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import {TriangleLocation} from 'notebook/tables/errors/ErrorTriangle';
import notifications from 'notifications';
import preferences from 'Preferences';
import {archiveAssayRuns, unarchiveAssayRuns, updateAssayRun} from 'results/assays/AssayRecordDAO';
import {
  RunValidationConnector,
  RunValidationConnectorEnhancedProps,
  RunValidationConnectorInputProps,
} from 'results/validation/RunValidationConnector';
import ValidationIndicator from 'results/validation/ValidationIndicator';
import areAllArchivableOutputFilesArchived from 'runs/areAllArchivableOutputFilesArchived';
import SchemaTagInputs from 'schemas/tags/SchemaTagInputs';
import {getTagsToSet} from 'schemas/tags/TagUtil';
import StudySearchInput from 'studies/StudySearchInput';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import EntryChip from 'ui-library/chips/EntryChip';
import {Title} from 'ui-library/Headers';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {readableTime} from 'util/DateUtil';

import {RunQuery, RunQueryVariables} from './__generated/RunQuery';
import {RunDataSourceSelector} from './RunDataSourceSelector';
import {RUN_QUERY} from './RunFileState';

type Run = ExtractByTypename<RunQuery['run'], 'RunNode'>;

const ACTION_ORIGIN = ActionOrigin.RUN_PAGE_TOOLBAR;

export function MetadataFieldsWrapper(props: {children: React.ReactNode}): JSX.Element {
  return (
    <Row bufferRowReset={true}>
      {React.Children.toArray(props.children).map((child: JSX.Element, i) => (
        <div key={`col-${i}`}>
          {i % 3 === 0 && <Col className="clearfix" key={`clearfix-${i}`} />}
          <Col className="resultsAttachment-assayRun-fieldSection" key={i} sm={4}>
            {child}
          </Col>
        </div>
      ))}
    </Row>
  );
}

interface RunDetailsProps extends RunValidationConnectorInputProps {
  run: Run;
  refetch: () => void;
}

export function RunDetails(props: RunDetailsProps & RunValidationConnectorEnhancedProps): JSX.Element {
  const originType = props.run.origin.type;
  const isWritable =
    BenchlingConfig.LAB_AUTOMATION_ALLOW_EDITING_API_RUNS || originType === AssayRecordOriginType.ELN;
  const tagsEditable =
    isWritable &&
    isFeatureEnabled({
      feature: StandardFeature.EDIT_TAGS,
      item: {id: props.run.apiId, permissions: props.run.permissions},
    });
  const fieldEditable =
    isWritable &&
    isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_EDIT}) &&
    isFeatureEnabled({
      feature: StandardFeature.EDIT,
      item: {id: props.run.apiId, permissions: props.run.permissions},
    }) &&
    props.run.automationInputFiles.filter((aif) => aif.status === Status.succeeded).length === 0 &&
    props.run.automationOutputFiles.filter((aof) => aof.status === Status.succeeded).length === 0;
  const labAutoEditable = isFeatureEnabled({
    feature: StandardFeature.EDIT,
    item: {id: props.run.apiId, permissions: props.run.permissions},
  });

  const handleUpdateTags = async (tags: Array<Tag>): Promise<void> => {
    const tagsToSet = getTagsToSet(tags);
    await Context.usingAsync(
      {
        analyticsMetadata: {
          actionOrigin: ActionOrigin.RUN_PAGE,
        },
      },
      null,
      async () => {
        await updateAssayRun(props.run.id, {
          tags: tagsToSet,
          connectionId: props.run.connection?.id ?? null,
          equipmentId: props.run.equipment?.id ?? null,
          studyIds: props.run.studyIds ?? [],
        });
      }
    );
    notifications.success('Run updated successfully.');
    props.refetch();
  };

  const handleUpdateRunFields = async (
    connectionId: string | null,
    equipmentId: string | null
  ): Promise<void> => {
    const tagsToSet = getTagsToSet(props.run.tagsJson);
    await Context.usingAsync(
      {
        analyticsMetadata: {
          actionOrigin: ActionOrigin.RUN_PAGE,
        },
      },
      null,
      async () => {
        await updateAssayRun(props.run.id, {
          tags: tagsToSet,
          connectionId,
          equipmentId,
          studyIds: props.run.studyIds ?? [],
        });
      }
    );
    notifications.success('Run updated successfully.');
    props.refetch();
  };

  return (
    <div className="react-tab-container">
      <div>
        <ValidationIndicator
          assayValidationToDisplay={props.assayRunValidation}
          location={TriangleLocation.RUN}
        />
        <div>
          <Row bufferRowReset={true}>
            <Col xs={6} className={css(styles.title)}>
              {props.run.schema.name}
            </Col>
            <Col xs={6}>
              <div className="u-justify-content-end">
                <ArchiveOrUnarchiveButton
                  className="metadata-header-button-spacing-left"
                  item={{...props.run, id: props.run.apiId, name: props.run.schema.name}}
                  isArchivable={
                    isFeatureEnabled({
                      feature: StandardFeature.ARCHIVE,
                      item: {id: props.run.apiId, permissions: props.run.permissions},
                    }) &&
                    areAllArchivableOutputFilesArchived(props.run.automationOutputFiles) &&
                    // We currently block runs that are in entries from being archived
                    // because the notebook does not handle rendering archived runs
                    !props.run.isInEntry
                  }
                  isUnarchivable={isFeatureEnabled({
                    feature: StandardFeature.UNARCHIVE,
                    item: {id: props.run.apiId, permissions: props.run.permissions},
                  })}
                  onArchive={async (ids: Array<string>, purpose: string) => {
                    assert(
                      ids.length === 1 && ids[0] === props.run.apiId,
                      'expected run ID to match current run'
                    );
                    await archiveAssayRuns([props.run.id], purpose);
                    props.refetch();
                  }}
                  getAnalyticsMetadata={() => ({
                    actionOrigin: ACTION_ORIGIN,
                    eventGroupId: makeAnalyticsEventId(),
                  })}
                  onUnarchive={(ids: Array<string>) => {
                    assert(
                      ids.length === 1 && ids[0] === props.run.apiId,
                      'expected run ID to match current run'
                    );
                    Context.using(
                      {
                        analyticsMetadata: {
                          actionOrigin: ACTION_ORIGIN,
                          eventGroupId: makeAnalyticsEventId(),
                        },
                      },
                      null,
                      async () => {
                        await unarchiveAssayRuns([props.run.id]);
                        props.refetch();
                      }
                    );
                  }}
                />
                <ExportAuditLogButton
                  className="metadata-header-button-spacing-left"
                  ids={[props.run.apiId]}
                />
                {Boolean(preferences.get('ENABLE_COPY_API_ID')) && (
                  <Tooltip container="body" placement={TooltipPlacement.TOP} title="Copy API ID">
                    <Button
                      className="metadata-header-button-spacing-left"
                      onClick={() => copyToClipboard(props.run.id, {showToastOnSuccess: true})}
                      icon="note-code"
                      size={ButtonSize.SMALL}
                      buttonStyle={ButtonStyle.TERTIARY_BLUE}
                    />
                  </Tooltip>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <div className="u-spacing-top">
        <MetadataFieldsWrapper>
          <>
            <Title>Creator</Title>
            <div>
              <Avatar className="u-mini-spacing-right" size={AvatarSize.XXSMALL} entity={props.run.creator} />
              {props.run.creator.name}
            </div>
          </>
          <>
            <Title>Created</Title>
            <div>{readableTime(props.run.createdAt)}</div>
          </>
          <>
            <Title>Origin</Title>
            <div>
              {props.run.origin.elnOrigin.entry?.id ? (
                <EntryChip id={props.run.origin.elnOrigin.entry.id} size={ChipSize.SMALL} />
              ) : (
                originType
              )}
            </div>
          </>
          <RunDataSourceSelector
            shouldRecordEquipment={props.run.shouldRecordEquipment}
            selectedEquipment={props.run.equipment ? props.run.equipment : null}
            onUpdateEquipment={(connectionId, equipmentId) => {
              if (props.run.schema.connectionSchema?.id && !equipmentId) {
                notifications.error(
                  'Cannot remove equipment from a run with a connection schema. Please select a new equipment.'
                );
              } else {
                handleUpdateRunFields(connectionId || (props.run.connection?.id ?? null), equipmentId);
              }
            }}
            connectionSchemaId={props.run.schema.connectionSchema?.id}
            connection={props.run.connection}
            onUpdateConnection={(connectionId) => {
              handleUpdateRunFields(connectionId, props.run.equipment?.id ?? null);
            }}
            fieldEditable={fieldEditable}
          />
          {BenchlingConfig.STUDIES_ENABLE && props.run.studyIds && (
            <>
              <Title>Studies</Title>
              <div>
                <StudySearchInput<'id'>
                  onChange={() => {}}
                  shouldAllowMultiple={true}
                  selectedItems={props.run.studyIds.map((studyId) => ({id: studyId}))}
                  isReadOnly={true}
                  hasNonInteractiveChips={false}
                />
              </div>
            </>
          )}
        </MetadataFieldsWrapper>
        <div className="u-spacing-top">
          <SchemaTagInputs
            className="bufferRowReset u-small-spacing-top"
            itemLinkAllowed={true}
            isWritable={tagsEditable}
            onUpdateTags={handleUpdateTags}
            tags={props.run.tagsJson}
          />
        </div>
      </div>
      <RunAutomationFileViewer
        runId={props.run.id}
        automationFiles={{
          automationInputFiles: graphQLArrayToREST('AutomationInputFile', props.run.automationInputFiles),
          automationOutputFiles: graphQLArrayToREST('AutomationOutputFile', props.run.automationOutputFiles),
          automationErrorFiles: graphQLArrayToREST('AutomationErrorFile', props.run.automationErrorFiles),
        }}
        locked={!labAutoEditable}
        isConnectionRun={props.run.schema.connectionSchema != null}
        connectionId={props.run.connection?.id ?? null}
        handleRefreshVersionedData={asyncNoop}
        handleUpdateAutomationFiles={props.refetch}
        getIdFromCid={undefined /* TODO(BNCH-66258): Display structured tables */}
        versionedStructuredTables={undefined /* TODO(BNCH-66258): Display structured tables */}
        viewer={false}
      />
    </div>
  );
}

class RunDetailsConnector extends RunValidationConnector<RunDetailsProps> {}
const ConnectedRunDetails = RunDetailsConnector.connect(RunDetails);

export default function QueryingRunDetails(props: {runId: string}): JSX.Element {
  const result = useBenchlingQuery<RunQuery, RunQueryVariables>(RUN_QUERY, {
    variables: {runId: props.runId},
  });

  if (result.type !== QueryResultType.SUCCESS) {
    return <LoadingFileScreen />;
  }
  if (isInaccessible(result.data.run)) {
    return <WorkspaceItemInaccessibleGuard inaccessibleInfo={result.data.run} modelType="run" />;
  } else {
    return (
      <ConnectedRunDetails
        run={result.data.run}
        refetch={() => result.refetch({runId: props.runId})}
        assayRunIdForValidationFetch={result.data.run.id}
      />
    );
  }
}

export const styles = StyleSheet.create({
  title: {
    fontSize: '17px',
  },
});
